import React, { useEffect, useRef, useState } from 'react';
import axios from '~/axiosWrapper';

import { useStoreState } from '~/store';
import Loader from '~/app/common/Loader';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import { API } from '~/app/routes';

import { connectivityService } from '~/data/connectivity/ConnectivityService';

type ConnectivityProviderComponent = React.FC;

const RECOVERY_POLL_MS = 10000;
const MANUAL_OFFLINE_TICK_MS = 1000;

const ConnectivityProvider: ConnectivityProviderComponent = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const networkStatus = useStoreState((state) => state.app.offline.networkStatus);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const manualExpiryRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (networkStatus === 1) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      pollRef.current = setInterval(() => {
        axios
          .get(`${API.projects}/last-used`)
          .then(() => {
            connectivityService.notifyNetworkRecovered();
            if (pollRef.current) {
              clearInterval(pollRef.current);
            }
          })
          .catch(() => undefined);
      }, RECOVERY_POLL_MS);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = undefined;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [networkStatus]);

  useEffect(() => {
    manualExpiryRef.current = setInterval(() => {
      connectivityService.refreshManualOfflineExpiry();
    }, MANUAL_OFFLINE_TICK_MS);
    return () => {
      if (manualExpiryRef.current) {
        clearInterval(manualExpiryRef.current);
      }
    };
  }, []);

  useDidMountEffect(() => {
    if (window['cordova']) {
      const handleOffline = () => connectivityService.setNetworkStatus(1);
      const handleOnline = () => connectivityService.setNetworkStatus(0);
      document.addEventListener('offline', handleOffline, false);
      document.addEventListener('online', handleOnline, false);
      connectivityService.applyNavigatorConnection();
      setTimeout(() => setLoading(false), 1000);
      return () => {
        document.removeEventListener('offline', handleOffline, false);
        document.removeEventListener('online', handleOnline, false);
      };
    }
    setLoading(false);
  });

  if (loading) {
    return <Loader size="32" />;
  }

  return <>{children}</>;
};

export default ConnectivityProvider;
